import React from 'react';
import { Cover1 } from '../components/organisms/Cover1/Cover1';
import { Text8 } from '../components/organisms/Text8/Text8';
import { Text2 } from '../components/organisms/Text2/Text2';
import { ContentCenter14 } from '../components/organisms/ContentCenter14/ContentCenter14';
import { Content9 } from '../components/organisms/Content9/Content9';
import client from '../tina/__generated__/client';
import { useTina } from 'tinacms/dist/react';
import { AboutMeQuery, Exact } from '../tina/__generated__/types';
import { Grid20 } from 'components/molecules/Grid20/Grid20';
import { MarkdownWithTypo } from 'components/organisms/MarkdownWithTypo/MarkdownWithTypo';
import { Flex } from '../components/atoms/Flex';
import { Heading } from '../shared/Typography';
import { Section } from 'react-headings';
import { NextSeo, NextSeoProps } from 'next-seo';

export default function AboutMe(props: {
  data: AboutMeQuery;
  query: string;
  variables: Exact<{ relativePath: string }>;
}) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  return (
    <>
      <NextSeo {...(data.aboutMe.seo as NextSeoProps)} />
      <Cover1 {...data.aboutMe.cover} />
      <Flex
        css={{
          flexDirection: 'column',
          paddingBlock: '$spacing-5x',
          paddingInline: '$spacingMainMobile',
          '@tabletAndUp': {
            paddingBlock: '$spacing-7x',
          },
        }}
      >
        <Flex
          css={{
            flexDirection: 'column',
            maxWidth: '$textContent',
            margin: 'auto',
          }}
        >
          <MarkdownWithTypo content={data.aboutMe.welcomeText} />
        </Flex>
      </Flex>

      <Grid20 items={data.aboutMe.memberships} />

      <Flex
        css={{
          flexDirection: 'column',
          paddingInline: '$spacingMainMobile',
          paddingBlock: '$spacing-5x',
        }}
      >
        <Flex
          css={{
            flexDirection: 'column',
            maxWidth: '$laptop',
            margin: 'auto',
            gap: '$spacing-5x',
            alignContent: 'stretch',
            justifyContent: 'center',
            '@tabletAndUp': {
              gap: '$spacing-10x',
              paddingBlock: '$spacing-8x',
            },
            '@laptopAndUp': {
              gap: '$spacing-15x',
            },
            '@desktopAndUp': {
              gap: '$spacing-1px',
            },
          }}
        >
          <Section
            component={
              <Heading
                variant={{ '@initial': 'h2', '@tabletAndUp': 'h2TabletAndUp' }}
              >
                {data.aboutMe.headlineCareer}
              </Heading>
            }
          >
            {data.aboutMe.contentComponents?.map((contentComponent, index) => (
              <Content9
                title={contentComponent?.headline}
                buttonHref={contentComponent?.buttonHref}
                buttonLabel={contentComponent?.buttonLabel}
                imageSrc={contentComponent?.imageSrc}
                imageAlt={contentComponent?.imageAlt}
                imagePosition={contentComponent?.imagePosition ?? undefined}
                textPosition={
                  contentComponent?.textPosition === 'left' ? 'left' : 'right'
                }
                key={'content9-' + index}
              >
                <MarkdownWithTypo content={contentComponent?.content} />
              </Content9>
            ))}
          </Section>
        </Flex>
      </Flex>

      <ContentCenter14
        content={data.aboutMe.callToAction?.content}
        href={data.aboutMe.callToAction?.linkHref}
        label={data.aboutMe.callToAction?.linkLabel}
      />
    </>
  );
}

export const getStaticProps = async () => {
  const pageResponse = await client.queries.aboutMe({
    relativePath: 'about-me.mdx',
  });

  return {
    props: {
      data: pageResponse.data,
      query: pageResponse.query,
      variables: pageResponse.variables,
    },
  };
};
